import type { ReactElement } from "react"
import { useRef, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import {
	IconBellOutline18,
	IconBellSlashOutline18,
	IconCircleCheckOutline18,
	IconCircleUserOutline18,
	IconClockSnoozeOutline18,
	IconCopyOutline18,
	IconEnvelopeOpenOutline18,
	IconEnvelopeOutline18,
	IconInboxOutline18,
	IconOffice3Outline18,
	IconSquareArrowUpRightOutline18,
	IconTagOutline18,
	IconTrashOutline18,
	IconUserOutline18,
} from "nucleo-ui-outline-18"

import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import {
	AlertDialog,
	ContextMenu,
	ContextMenuContent,
	ContextMenuItem,
	ContextMenuSeparator,
	ContextMenuTrigger,
	LucideLinearIcon,
	PriorityIcons,
	SlackIcon,
} from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import type { MenuThread } from "./thread-menu-submenus"
import type {
	BulkThreadMenuActions,
	ThreadMenuActions,
} from "./use-thread-menu-actions"
import { useSubscriptionToggle } from "@/components/notifications/subscribe-button"
import { hasLinearConnection } from "@/lib/linear-connection"
import { getPortalBaseUrl } from "@/lib/portal-base-url"
import { useRole } from "@/lib/useRole"
import { useSlackConnections, useZeroWorkspace } from "@/lib/zero"
import { SnoozeDialog } from "@/screens/app/threads/components/center-content/snooze-dialog"
import { getSlackMessageLink } from "@/screens/app/threads/lib/slack-message-link"
import { useThreadsMultiSelectState } from "@/screens/app/threads/multi-select/threads-multi-select-state"
import {
	THREAD_MENU_COPY as copy,
	bulkMenuState,
	bulkMenuTargets,
	handleMenuShortcut,
	isDoneState,
	threadCopyOptions,
	threadCount,
	threadPath,
} from "./thread-menu-model"
import {
	AssigneeContent,
	CopyContent,
	CustomerContent,
	IssueProjectContent,
	LazySub,
	PersonContent,
	PriorityContent,
	SnoozeContent,
	TagsContent,
} from "./thread-menu-submenus"
import {
	useBulkThreadMenuActions,
	useThreadMenuActions,
} from "./use-thread-menu-actions"

// The priority glyphs type their stroke as a number; the menu row passes plain SVG props.
const MediumPriorityIcon = PriorityIcons.MEDIUM
function PriorityMenuIcon({ className }: { className?: string }) {
	return <MediumPriorityIcon className={className} />
}

interface ThreadListContextMenuProps {
	/** The element holding the rows; each row carries `data-thread-id`. */
	children: ReactElement
	/** The live row, so a submenu reflects a change while it stays open. */
	getThread: (threadId: string) => MenuThread | undefined
}

/**
 * One right-click menu for a whole thread list, like Linear's: it reads which row was
 * clicked from `data-thread-id`, so rows mount nothing extra.
 */
export function ThreadListContextMenu({
	children,
	getThread,
}: ThreadListContextMenuProps) {
	const [targetId, setTargetId] = useState<string | null>(null)
	// A row can leave the list mid-action (marked done); the menu keeps the last copy.
	const lastSeen = useRef<MenuThread | null>(null)
	const live = targetId ? getThread(targetId) : undefined
	if (live) lastSeen.current = live
	const thread = targetId ? (live ?? lastSeen.current) : null

	const actions = useThreadMenuActions()
	const bulkActions = useBulkThreadMenuActions()
	const [pendingDelete, setPendingDelete] = useState<MenuThread | null>(null)
	const [snoozeTarget, setSnoozeTarget] = useState<MenuThread | null>(null)
	// Set when the clicked row is one of several selected; the menu then acts on all of them.
	const [bulkIds, setBulkIds] = useState<string[] | null>(null)
	const [pendingBulkDelete, setPendingBulkDelete] = useState<string[] | null>(
		null,
	)
	const [bulkSnoozeIds, setBulkSnoozeIds] = useState<string[] | null>(null)

	return (
		<>
			<ContextMenu>
				<ContextMenuTrigger
					asChild
					onContextMenu={(event) => {
						const row =
							event.target instanceof Element
								? event.target.closest<HTMLElement>("[data-thread-id]")
								: null
						const id = row?.dataset.threadId
						const found = id ? getThread(id) : undefined
						if (!id || !found) {
							event.preventDefault()
							return
						}
						lastSeen.current = found
						setTargetId(id)
						setBulkIds(
							bulkMenuTargets(
								useThreadsMultiSelectState.getState().threadIds,
								id,
							),
						)
					}}
				>
					{children}
				</ContextMenuTrigger>
				{bulkIds ? (
					<BulkThreadMenuContent
						ids={bulkIds}
						getThread={getThread}
						actions={bulkActions}
						onDelete={setPendingBulkDelete}
						onPickSnoozeDate={setBulkSnoozeIds}
					/>
				) : (
					thread && (
						<ThreadMenuContent
							key={thread.id}
							thread={thread}
							actions={actions}
							onDelete={setPendingDelete}
							onPickSnoozeDate={setSnoozeTarget}
						/>
					)
				)}
			</ContextMenu>

			<AlertDialog
				isOpen={!!pendingDelete}
				onOpenChange={(open) => {
					if (!open) setPendingDelete(null)
				}}
				confirmText={copy.delete}
				title={copy.deleteTitle}
				description={copy.deleteDescription}
				onCancel={() => setPendingDelete(null)}
				onConfirm={async () => {
					const target = pendingDelete
					setPendingDelete(null)
					if (target) await actions.remove(target)
				}}
			/>

			<AlertDialog
				isOpen={!!pendingBulkDelete}
				onOpenChange={(open) => {
					if (!open) setPendingBulkDelete(null)
				}}
				confirmText={copy.delete}
				title={copy.deleteTitle}
				description={copy.bulkDeleteDescription(pendingBulkDelete?.length ?? 0)}
				onCancel={() => setPendingBulkDelete(null)}
				onConfirm={async () => {
					const ids = pendingBulkDelete
					setPendingBulkDelete(null)
					if (ids) await bulkActions.remove(ids)
				}}
			/>

			<SnoozeDialog
				open={!!bulkSnoozeIds}
				onOpenChange={(open) => {
					if (!open) setBulkSnoozeIds(null)
				}}
				onSelect={(value) => {
					if (bulkSnoozeIds) void bulkActions.snooze(bulkSnoozeIds, value)
					setBulkSnoozeIds(null)
				}}
			/>

			<SnoozeDialog
				open={!!snoozeTarget}
				onOpenChange={(open) => {
					if (!open) setSnoozeTarget(null)
				}}
				onSelect={(value) => {
					if (snoozeTarget) actions.snooze(snoozeTarget, value)
					setSnoozeTarget(null)
				}}
			/>
		</>
	)
}

interface ThreadMenuContentProps {
	thread: MenuThread
	actions: ThreadMenuActions
	onDelete: (thread: MenuThread) => void
	onPickSnoozeDate: (thread: MenuThread) => void
}

// Ordered like Linear's issue menu: state and properties, then inbox actions, then copy and open, then delete.
export function ThreadMenuContent({
	thread,
	actions,
	onDelete,
	onPickSnoozeDate,
}: ThreadMenuContentProps) {
	const { role } = useRole()
	const canEdit = role !== "VIEWER"
	const workspace = useZeroWorkspace()
	const done = isDoneState(thread.state)
	const portalBaseUrl =
		workspace?.customerPortalSettings?.isEnabled === true
			? getPortalBaseUrl(workspace)
			: null
	const copyOptions = threadCopyOptions(thread, {
		origin: window.location.origin,
		portalBaseUrl,
	})

	return (
		<ContextMenuContent
			className="w-60"
			data-testid="thread-context-menu"
			onKeyDown={handleMenuShortcut}
		>
			{canEdit && (
				<>
					<ContextMenuItem
						Icon={done ? IconInboxOutline18 : IconCircleCheckOutline18}
						shortcut={ShortcutSymbols.E}
						data-shortcut="e"
						onSelect={() => actions.toggleDone(thread)}
					>
						{done ? copy.moveToInbox : copy.markDone}
					</ContextMenuItem>
					<LazySub
						label={copy.priority}
						trigger={{
							Icon: PriorityMenuIcon,
							shortcut: ShortcutSymbols.P,
							shortcutKey: "p",
						}}
					>
						{() => (
							<PriorityContent
								painLevel={thread.painLevel}
								onSelect={(painLevel) =>
									void actions.update(thread, { painLevel }, "priority")
								}
							/>
						)}
					</LazySub>
					<LazySub
						label={copy.assignee}
						trigger={{
							Icon: IconCircleUserOutline18,
							shortcut: ShortcutSymbols.A,
							shortcutKey: "a",
						}}
					>
						{() => (
							<AssigneeContent
								assigneeId={thread.assigneeId}
								onSelect={(assigneeId) =>
									void actions.update(thread, { assigneeId }, "assign")
								}
							/>
						)}
					</LazySub>
					<LazySub
						label={copy.tags}
						trigger={{
							Icon: IconTagOutline18,
							shortcut: ShortcutSymbols.T,
							shortcutKey: "t",
						}}
					>
						{() => <TagsContent thread={thread} />}
					</LazySub>
					{hasLinearConnection(workspace) && (
						<LazySub
							label={copy.issueOrProject}
							trigger={{
								Icon: LucideLinearIcon,
								shortcut: ShortcutSymbols.C,
								shortcutKey: "c",
							}}
						>
							{() => <IssueProjectContent thread={thread} />}
						</LazySub>
					)}
					<LazySub label={copy.person} trigger={{ Icon: IconUserOutline18 }}>
						{() => (
							<PersonContent
								contactId={thread.contactId}
								onSelect={(contact) =>
									void actions.update(
										thread,
										{
											contactId: contact?.id ?? null,
											companyId: contact?.companyId ?? null,
										},
										"person",
									)
								}
							/>
						)}
					</LazySub>
					<LazySub
						label={copy.customer}
						trigger={{ Icon: IconOffice3Outline18 }}
					>
						{() => (
							<CustomerContent
								companyId={thread.companyId}
								onSelect={(companyId) =>
									void actions.update(thread, { companyId }, "customer")
								}
							/>
						)}
					</LazySub>

					<ContextMenuSeparator />

					<ContextMenuItem
						Icon={
							thread.isRead ? IconEnvelopeOutline18 : IconEnvelopeOpenOutline18
						}
						shortcut={ShortcutSymbols.U}
						data-shortcut="u"
						onSelect={() =>
							void actions.update(
								thread,
								{ isRead: !thread.isRead },
								thread.isRead ? "mark-unread" : "mark-read",
							)
						}
					>
						{thread.isRead ? copy.markUnread : copy.markRead}
					</ContextMenuItem>
					{thread.state === "SNOOZED" ? (
						<ContextMenuItem
							Icon={IconClockSnoozeOutline18}
							shortcut={ShortcutSymbols.H}
							data-shortcut="h"
							onSelect={() => actions.unsnooze(thread)}
						>
							{copy.unsnooze}
						</ContextMenuItem>
					) : (
						<LazySub
							label={copy.snooze}
							trigger={{
								Icon: IconClockSnoozeOutline18,
								shortcut: ShortcutSymbols.H,
								shortcutKey: "h",
							}}
						>
							{() => (
								<SnoozeContent
									onSelect={(value) => actions.snooze(thread, value)}
									onPickDate={() => onPickSnoozeDate(thread)}
								/>
							)}
						</LazySub>
					)}
					<SubscribeItem threadId={thread.id} />

					<ContextMenuSeparator />
				</>
			)}

			<LazySub label={copy.copy} trigger={{ Icon: IconCopyOutline18 }}>
				{() => (
					<CopyContent
						options={copyOptions}
						onCopy={(option) => actions.copy(option)}
					/>
				)}
			</LazySub>
			<ContextMenuItem
				Icon={IconSquareArrowUpRightOutline18}
				onSelect={() =>
					window.open(threadPath(thread.id), "_blank", "noopener")
				}
			>
				{copy.openInNewTab}
			</ContextMenuItem>
			{thread.slackChannelId && <OpenInSlackItem thread={thread} />}

			{canEdit && (
				<>
					<ContextMenuSeparator />
					<ContextMenuItem
						Icon={IconTrashOutline18}
						shortcut={`${ShortcutSymbols.CMD}${ShortcutSymbols.BACKSPACE}`}
						data-shortcut="mod+backspace"
						onSelect={() => onDelete(thread)}
					>
						{copy.delete}
					</ContextMenuItem>
				</>
			)}
		</ContextMenuContent>
	)
}

interface BulkThreadMenuContentProps {
	ids: string[]
	getThread: (threadId: string) => MenuThread | undefined
	actions: BulkThreadMenuActions
	onDelete: (ids: string[]) => void
	onPickSnoozeDate: (ids: string[]) => void
}

/** The menu over a multi-selection: only the changes that make sense for many threads at once. */
export function BulkThreadMenuContent({
	ids,
	getThread,
	actions,
	onDelete,
	onPickSnoozeDate,
}: BulkThreadMenuContentProps) {
	const { role } = useRole()
	const threads = ids
		.map((id) => getThread(id))
		.filter((thread): thread is MenuThread => !!thread)
	const state = bulkMenuState(threads)

	return (
		<ContextMenuContent
			className="w-60"
			data-testid="thread-bulk-context-menu"
			onKeyDown={handleMenuShortcut}
		>
			<div className="px-2 py-1.5 text-xs tabular-nums text-tertiary">
				{threadCount(ids.length)} selected
			</div>
			{role !== "VIEWER" && (
				<>
					<ContextMenuItem
						Icon={state.allDone ? IconInboxOutline18 : IconCircleCheckOutline18}
						shortcut={ShortcutSymbols.E}
						data-shortcut="e"
						onSelect={() => void actions.setDone(ids, !state.allDone)}
					>
						{state.allDone ? copy.moveToInbox : copy.markDone}
					</ContextMenuItem>
					<LazySub
						label={copy.priority}
						trigger={{
							Icon: PriorityMenuIcon,
							shortcut: ShortcutSymbols.P,
							shortcutKey: "p",
						}}
					>
						{() => (
							<PriorityContent
								painLevel={state.painLevel as MenuThread["painLevel"] | null}
								onSelect={(painLevel) =>
									void actions.setPriority(ids, painLevel)
								}
							/>
						)}
					</LazySub>
					<LazySub
						label={copy.assignee}
						trigger={{
							Icon: IconCircleUserOutline18,
							shortcut: ShortcutSymbols.A,
							shortcutKey: "a",
						}}
					>
						{() => (
							<AssigneeContent
								assigneeId={state.assigneeId}
								onSelect={(assigneeId) => void actions.assign(ids, assigneeId)}
							/>
						)}
					</LazySub>

					<ContextMenuSeparator />

					<ContextMenuItem
						Icon={
							state.allRead ? IconEnvelopeOutline18 : IconEnvelopeOpenOutline18
						}
						shortcut={ShortcutSymbols.U}
						data-shortcut="u"
						onSelect={() => void actions.setRead(ids, !state.allRead)}
					>
						{state.allRead ? copy.markUnread : copy.markRead}
					</ContextMenuItem>
					{state.allSnoozed ? (
						<ContextMenuItem
							Icon={IconClockSnoozeOutline18}
							shortcut={ShortcutSymbols.H}
							data-shortcut="h"
							onSelect={() => void actions.unsnooze(ids)}
						>
							{copy.unsnooze}
						</ContextMenuItem>
					) : (
						<LazySub
							label={copy.snooze}
							trigger={{
								Icon: IconClockSnoozeOutline18,
								shortcut: ShortcutSymbols.H,
								shortcutKey: "h",
							}}
						>
							{() => (
								<SnoozeContent
									onSelect={(value) => void actions.snooze(ids, value)}
									onPickDate={() => onPickSnoozeDate(ids)}
								/>
							)}
						</LazySub>
					)}

					<ContextMenuSeparator />
					<ContextMenuItem
						Icon={IconTrashOutline18}
						shortcut={`${ShortcutSymbols.CMD}${ShortcutSymbols.BACKSPACE}`}
						data-shortcut="mod+backspace"
						onSelect={() => onDelete(ids)}
					>
						{copy.delete}
					</ContextMenuItem>
				</>
			)}
		</ContextMenuContent>
	)
}

function SubscribeItem({ threadId }: { threadId: string }) {
	const { isSubscribed, toggle } = useSubscriptionToggle("thread", threadId)
	return (
		<ContextMenuItem
			Icon={isSubscribed ? IconBellSlashOutline18 : IconBellOutline18}
			onSelect={() => void toggle()}
		>
			{isSubscribed ? copy.unsubscribe : copy.subscribe}
		</ContextMenuItem>
	)
}

function OpenInSlackItem({ thread }: { thread: MenuThread }) {
	const connections = useSlackConnections()
	// Only a top-level message with no reply yet needs the true first message for its link.
	const [oldest] = useQuery(
		queries.threadOldestMessages({ threadId: thread.id }),
		{ ttl: "10m", enabled: !thread.slackReplyId },
	)
	if (connections.length === 0) return null
	const slackMessages = oldest?.slackMessages ?? thread.slackMessages
	const teamId = slackMessages.find((message) => message.teamId)?.teamId
	const teamUrl = (
		connections.find((row) => row.teamId === teamId) ?? connections[0]
	)?.teamUrl
	const link = getSlackMessageLink({
		teamUrl,
		slackChannelId: thread.slackChannelId,
		slackReplyId: thread.slackReplyId,
		slackMessages,
	})
	return (
		<ContextMenuItem
			Icon={SlackIcon}
			shortcut={`${ShortcutSymbols.ALT}${ShortcutSymbols.S}`}
			data-shortcut="alt+s"
			disabled={!link}
			onSelect={() => window.open(link, "_blank", "noopener")}
		>
			{copy.openInSlack}
		</ContextMenuItem>
	)
}
